import React from 'react';
import Button from '../../commons/actionButton/actionButton';
import styles from './chargeReconciliation.scss';

import eyeIcon from '../../../assets/images/eyeIcon.svg';
import ticketIcon from '../../../assets/images/ticket_black_icon.svg';
import configProd from '../../../configs/configProd';
import config from '../../../configs/envConfig';
import Chip from '../../commons/chip';
import { zoomTableActionsConfig } from '../../commons/zoomTable/zoomTableConfig';
import {
  escalationCategoryMapper,
  escalationStatusENUM,
  statusOptions
} from './constants';

export function formatDate(inputDate) {
  const date = new Date(inputDate);

  const options = {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  };

  return date.toLocaleDateString('en-GB', options);
}

export const RenderHeader = ({ line1, line2 }) => {
  return (
    <div className={styles.tableHeader}>
      <div>{line1}</div>
      {line2 && <div>{line2}</div>}
    </div>
  );
};

export const RenderTextCell = ({
  value,
  isPrimary = true,
  icon,
  csx = {},
  onClick = null
}) => {
  return (
    <div
      style={{ ...csx, cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <div
        className={`${isPrimary ? styles.primaryText : styles.secondaryText} ${
          styles.tableData
        }`}
      >
        {value}
      </div>
      {icon && (
        <img
          src={icon}
          className={styles.icon}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
        />
      )}
    </div>
  );
};

const withLink = WrappedComponent => ({ to, target = '_blank', ...props }) => {
  if (!to) {
    return <WrappedComponent {...props} />;
  }

  return (
    <a href={to} target={target} style={{ textDecoration: 'none' }}>
      <WrappedComponent {...props} />
    </a>
  );
};

export const RenderLink = withLink(RenderTextCell);

export const RenderAmountCell = ({ value, icon, onAmountClick = null }) => {
  return (
    <div className={styles.amountCellWrapper} onClick={onAmountClick}>
      <div className={styles.amountField}>{value}</div>
      {icon && <img src={icon} className={styles.eyeIconImg} />}
    </div>
  );
};

export const chargeReconciliationTableConfig = {
  columns: [
    {
      Header: <RenderHeader line1={'CN NUMBER'} />,
      key: 'cnote',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <div>
            <RenderLink
              to={`bulktrackconsignment/${row.original.cnote}`}
              value={row.original.cnote || '-'}
              isPrimary={false}
            />
            <RenderTextCell
              value={
                row.original.clientCode ? `(${row.original.clientCode})` : '-'
              }
            />
          </div>
        );
      }
    },
    {
      Header: <RenderHeader line1={'CN Booking'} line2={'Details'} />,
      key: 'bookedCnWeight',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <div>
            <RenderTextCell
              value={
                row.original.bookedCnVolume
                  ? `${row.original.bookedCnVolume} cu.ft`
                  : '-'
              }
            />
            <RenderTextCell
              value={
                row.original.bookedCnWeight
                  ? `${row.original.bookedCnWeight} Kg`
                  : '-'
              }
            />
          </div>
        );
      }
    },
    {
      Header: <RenderHeader line1={'ESCALATION'} line2={'CATEGORY'} />,
      key: 'escalationCategory',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <div>
            <RenderTextCell
              value={
                row.original && row.original.escalationCategory
                  ? escalationCategoryMapper[row.original.escalationCategory]
                  : '-'
              }
            />
          </div>
        );
      }
    },
    {
      Header: <RenderHeader line1={'UPDATED QC'} line2={'DETAILS'} />,
      key: 'updatedCnWeight',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <div>
            <RenderTextCell
              value={
                row.original.updatedCnVolume
                  ? `${row.original.updatedCnVolume} cu.ft`
                  : '-'
              }
            />
            <RenderTextCell
              value={
                row.original.updatedCnWeight
                  ? `${row.original.updatedCnWeight} Kg`
                  : '-'
              }
            />
          </div>
        );
      }
    },
    {
      Header: <RenderHeader line1={'REPORTED'} line2={'AT'} />,
      key: 'lastReportedAt',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <RenderTextCell
            value={
              row.original.lastReportedAt
                ? formatDate(row.original.lastReportedAt)
                : '-'
            }
          />
        );
      }
    },
    {
      Header: <RenderHeader line1={'ADDITIONAL'} line2={'AMOUNT'} />,
      key: 'deltaAmount',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <>
            {row.original.deltaAmount && (
              <RenderAmountCell
                value={`RS. ${Math.round(row.original.deltaAmount)}`}
                icon={eyeIcon}
                onAmountClick={() =>
                  zoomTableActionsConfig.CHARGE_RECONCILIATION.actions.onAmoutClick(
                    row
                  )
                }
              />
            )}
          </>
        );
      }
    },
    {
      Header: <RenderHeader line1={'ESCALATION'} line2={'STATUS'} />,
      key: 'escalationStatus',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <>
            {row.original.escalationStatus ? (
              <RenderTextCell
                isPrimary={false}
                icon={ticketIcon}
                value={
                  statusOptions.find(
                    item => item.value === row.original.escalationStatus
                  ).label || ''
                }
                onClick={
                  row.original.escalationStatus !== escalationStatusENUM.CREATED
                    ? () =>
                        zoomTableActionsConfig.CHARGE_RECONCILIATION.actions.onStatusClick(
                          row
                        )
                    : undefined
                }
                csx={{ display: 'flex', alignItems: 'center', gap: '2px' }}
              />
            ) : (
              <div>--</div>
            )}
          </>
        );
      }
    },
    {
      Header: (
        <RenderHeader line1={'CONCERNED AT |'} line2={'ACTION TAKEN AT'} />
      ),
      key: 'concernRaisedAt',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <div>
            <RenderTextCell
              value={
                row.original.concernRaisedAt
                  ? formatDate(row.original.concernRaisedAt)
                  : '-'
              }
            />
            <RenderTextCell
              value={
                row.original.actionTakenAt
                  ? formatDate(row.original.actionTakenAt)
                  : '-'
              }
            />
          </div>
        );
      }
    },
    {
      Header: <RenderHeader line1={'ACTIONS'} />,
      key: '',
      headerClassName: `zt-custom-th flex-14 ${styles.tabletd}`,
      sortable: false,
      className: `zt-custom-td flex-14 ${styles.tabletd}`,
      Cell: row => {
        return (
          <>
            {row.original.isVOCUSER
              ? row.original.escalationStatus === 'IN_PROGRESS' && (
                  <Button
                    buttonText="RESOLVE"
                    csx={{ paddingInline: '10px' }}
                    isDisabled={row.original.isLoading}
                    disabledClass="disabled-button"
                    onClick={() =>
                      zoomTableActionsConfig.CHARGE_RECONCILIATION.actions.onResolveClick(
                        row
                      )
                    }
                  />
                )
              : row.original.isEscalationAllowed && (
                  <>
                    <Button
                      buttonText={
                        row.original.escalationStatus !== 'RESOLVED'
                          ? 'ESCALATE'
                          : 'Re-ESCALATE'
                      }
                      isDisabled={row.original.isLoading}
                      disabledClass="disabled-button"
                      csx={{ paddingInline: '10px' }}
                      onClick={() =>
                        zoomTableActionsConfig.CHARGE_RECONCILIATION.actions.onActionBtnClick(
                          row
                        )
                      }
                    />
                  </>
                )}
          </>
        );
      }
    }
  ],
  className: 'zoomTableLegacyWrapper',
  noDataText: 'No Record found',
  style: { maxHeight: 'calc(100vh - 21rem)' }
};

export const chargeReconciliationAmountTableConfig = {
  columns: [
    {
      Header: <RenderHeader line1={'Charge Type'} />,
      key: 'chargeType',
      headerClassName: 'zt-custom-th',
      sortable: false,
      className: 'zt-custom-td',
      Cell: row => {
        return <div>{row.original.chargeType}</div>;
      }
    },
    {
      Header: <RenderHeader line1={'QC Date'} />,
      key: 'firstReportedAt',
      headerClassName: 'zt-custom-th',
      sortable: false,
      className: 'zt-custom-td',
      Cell: row => {
        return (
          <div>
            {row.original.firstReportedAt
              ? formatDate(row.original.firstReportedAt)
              : '-'}
          </div>
        );
      }
    },
    {
      Header: <RenderHeader line1={'Re-QC Date'} />,
      key: 'lastReportedAt',
      headerClassName: 'zt-custom-th',
      sortable: false,
      className: 'zt-custom-td',
      Cell: row => {
        return (
          <div>
            {row.original.lastReportedAt
              ? formatDate(row.original.lastReportedAt)
              : '-'}
          </div>
        );
      }
    },
    {
      Header: <RenderHeader line1={'Additional Amount'} />,
      key: 'deltaAmount',
      headerClassName: 'zt-custom-th',
      sortable: false,
      className: 'zt-custom-td',
      Cell: row => {
        return <div>{row.original.deltaAmount}</div>;
      }
    }
  ],
  className: 'zoomTableLegacyWrapper',
  noDataText: 'No data found',
  style: { maxHeight: 'calc(100vh - 21rem)' }
};

export const tickingStgUrl = `${window.location.origin}/${config.deploymentEnvType}/ticketing-ui/#/`;
export const tickingUrl =
  config.envioroment === 'production' ? configProd.ticketingUrl : tickingStgUrl;

export const chargeReconciliationTableActionConfig = {};
export const chargeReconciliationAmountTableActionConfig = {};

export const tableConfigName = 'CHARGE_RECONCILIATION';
export const chargeReconciliationAmountTableConfigName =
  'CHARGE_RECONCILIATION_AMOUNT';
